/**
 * The Care Concierge self-pay fee for a firm or a single advisor (Feature 106).
 *
 * Sits directly under the on/off switch in both editors because the two are agreed together: a
 * firm is switched on and told its rate in the same conversation. Like the switch, it saves on
 * its own — a negotiated price taking effect is not a draft edit waiting on a Save button.
 *
 * Blank means inherit. The field shows what that inherits as, so an admin clearing a firm's rate
 * sees "$350" come back rather than an empty box with no idea what clients will be charged.
 */

import { useEffect, useState } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { useToast } from '@/shared/components/Toast';
import {
  getAdvisorConciergeFee,
  getPartnerCompanyConciergeFee,
  setAdvisorConciergeFee,
  setPartnerCompanyConciergeFee,
  type CareConciergeFee,
} from '../../services/careConciergeCreditApi';

const errorMessage = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
  (error as { message?: string })?.message ??
  fallback;

const centsToInput = (cents: number | null): string =>
  cents === null ? '' : (cents / 100).toString();

/** "$350" for whole dollars, "$349.50" otherwise — matches the server's formatting. */
const dollarsLabel = (cents: number): string => {
  const dollars = cents / 100;
  return `$${dollars.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(dollars) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

/** "350" → 35000, "349.5" → 34950; null for blank; NaN for junk, which the caller refuses. */
const inputToCents = (raw: string): number | null => {
  const trimmed = raw.replace(/[$,\s]/g, '');
  if (trimmed === '') return null;
  if (!/^\d+(?:\.\d{1,2})?$/.test(trimmed)) return Number.NaN;
  return Math.round(Number(trimmed) * 100);
};

type CareConciergeFeeFieldProps =
  | { owner: 'partner-company'; ownerId: string }
  | { owner: 'advisor'; ownerId: string };

export const CareConciergeFeeField = ({ owner, ownerId }: CareConciergeFeeFieldProps) => {
  const { showToast } = useToast();
  const [fee, setFee] = useState<CareConciergeFee | null>(null);
  const [draft, setDraft] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [busy, setBusy] = useState(false);

  const isFirm = owner === 'partner-company';

  useEffect(() => {
    let active = true;

    const read = isFirm ? getPartnerCompanyConciergeFee : getAdvisorConciergeFee;
    read(ownerId)
      .then((result) => {
        if (!active) return;
        setFee(result);
        setDraft(centsToInput(result.selfPayFeeCents));
        setLoadError(null);
      })
      .catch((error) => {
        if (active) setLoadError(errorMessage(error, 'Could not read the consultation fee.'));
      });

    return () => {
      active = false;
    };
  }, [isFirm, ownerId, reloadToken]);

  const save = async (cents: number | null) => {
    if (cents !== null && (!Number.isFinite(cents) || cents <= 0)) {
      showToast('Enter a dollar amount above zero, or clear the field to inherit.', 'error');
      return;
    }
    setBusy(true);
    try {
      const write = isFirm ? setPartnerCompanyConciergeFee : setAdvisorConciergeFee;
      const result = await write(ownerId, cents);
      setFee(result);
      setDraft(centsToInput(result.selfPayFeeCents));
      showToast(
        result.selfPayFeeCents === null
          ? `Fee cleared — clients will be quoted ${result.effectiveFee}.`
          : `Self-pay fee set to ${result.effectiveFee}.`,
        'success',
      );
    } catch (error) {
      showToast(errorMessage(error, 'Could not save the fee.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="mb-4 rounded-md border border-destructive/40 p-3">
        <p className="text-sm text-destructive">{loadError}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2"
          onClick={() => setReloadToken((token) => token + 1)}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (fee === null) {
    return <p className="text-sm text-muted-foreground">Loading the consultation fee…</p>;
  }

  const dirty = inputToCents(draft) !== fee.selfPayFeeCents;
  const inheritedLabel = dollarsLabel(fee.inheritedFeeCents);
  // The self-pay Calendly event type names the platform default in its title, so any other
  // effective fee is contradicted on the booking page until that event type is reconciled.
  const calendlyDisagrees = fee.effectiveFeeCents !== fee.platformDefaultFeeCents;
  const platformDefaultLabel = dollarsLabel(fee.platformDefaultFeeCents);

  return (
    <div className="mb-4 rounded-md border border-border p-3">
      <Label htmlFor="cc-self-pay-fee">Self-pay consultation fee</Label>
      <p className="mt-1 text-xs text-muted-foreground">
        {fee.selfPayFeeCents === null
          ? `Inheriting ${inheritedLabel}${isFirm ? ' (platform default)' : ' from the firm'}. Clients are quoted ${fee.effectiveFee}.`
          : `Set here. Clients are quoted ${fee.effectiveFee}; clear to inherit ${inheritedLabel}.`}
      </p>
      {calendlyDisagrees && (
        <p role="alert" className="mt-2 text-xs text-amber-700">
          The self-pay booking page (Calendly) still advertises {platformDefaultLabel} in its event
          title. Reconcile that event type before clients at this rate are invited, or they will
          see {fee.effectiveFee} in the email and {platformDefaultLabel} when they book.
        </p>
      )}
      <div className="mt-2 flex items-center gap-2">
        <span className="text-sm text-muted-foreground">$</span>
        <Input
          id="cc-self-pay-fee"
          inputMode="decimal"
          className="w-32"
          placeholder={(fee.inheritedFeeCents / 100).toString()}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          disabled={busy}
        />
        <Button
          type="button"
          size="sm"
          onClick={() => void save(inputToCents(draft))}
          disabled={busy || !dirty}
        >
          Save
        </Button>
        {fee.selfPayFeeCents !== null && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void save(null)}
            disabled={busy}
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
};
